<template>
  <v-app class="page">
    <v-container fluid class="fill-height pa-0">
      <v-row class="fill-height">
        <!-- LEFT: Login Form -->
        <v-col cols="12" md="4" class="d-flex justify-center align-center pa-0">
          <v-card class="pa-6" width="100%" max-width="400" flat>
            <!-- Logo -->
            <v-row justify="center" class="mb-6">
              <img :src="logo" height="80" contain />
            </v-row>

            <!-- Form -->
            <v-form @submit.prevent="loginMethod">
              <v-text-field
                v-model="login.email"
                label="Email"
                type="email"
                prepend-inner-icon="mdi-email"
                required
                variant="solo"
                persistent-placeholder
              />

              <v-text-field
                v-model="login.password"
                :type="showPassword ? 'text' : 'password'"
                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                label="Password"
                prepend-inner-icon="mdi-lock"
                required
                variant="solo"
                persistent-placeholder
                @click:append-inner="showPassword = !showPassword"
              />
              <v-select
                v-model="language"
                :items="selectLanguage"
                item-title="lang"
                item-value="locale"
                label="Select Language"
                variant="solo"
                prepend-inner-icon="mdi-web"
                dense
              />

              <!-- Alerts -->
              <v-alert
                v-if="alert.value"
                type="error"
                dense
                variant="tonal"
                border="bottom"
                class="my-2"
              >
                {{ alert.message }}
              </v-alert>

              <v-alert
                v-if="alert_s.value"
                type="success"
                dense
                variant="tonal"
                border="bottom"
                class="my-2"
              >
                {{ alert_s.message }}
              </v-alert>
              <v-row justify="start" class="mb-4 ml-1">
                <a @click="goToReset()">
                  {{ $t("forgot_password") }}
                </a>
              </v-row>
              <!-- Login Button -->
              <v-row justify="center" class="mt-4">
                <v-btn
                  :loading="loading"
                  color="red-darken-4"
                  dark
                  type="submit"
                  class="w-100"
                >
                  <v-icon left class="mx-1">mdi-login</v-icon>
                  Login
                </v-btn>
              </v-row>
              <maintenance-banner class="mt-6" />
            </v-form>
          </v-card>
        </v-col>

        <!-- RIGHT: Background image, only for md+ screens -->
        <v-col cols="12" md="8" class="d-none d-md-flex pa-0">
          <div
            class="bg-image"
            :style="{ backgroundImage: `url(${bgImage})` }"
          ></div>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import determineIpLocation from "../utils/determineIpLocation";
import logoImg from "@/assets/logo_dark.png";
import bg from "@/assets/login_background0.png";
import MaintenanceBanner from "@/components/MaintenanceBanner.vue";

export default {
  name: "Login",
  components: { MaintenanceBanner },
  data() {
    return {
      logo: logoImg,
      bgImage: bg,
      showPassword: false,
      login: {
        email: "",
        password: "",
      },
      language: "it",
      selectLanguage: [
        { lang: "Italiano", locale: "it" },
        { lang: "English", locale: "en" },
      ],
      alert: { value: false, message: "" },
      alert_s: { value: false, message: "" },
      loading: false,
    };
  },
  watch: {
    "$route.fullPath": {
      immediate: true,
      handler() {
        this.checkAuthMessage();
      },
    },
    language: {
      handler(value) {
        localStorage.setItem("language", value);
        this.$store.dispatch("languageAction", value);
        this.$i18n.locale = this.$store.getters.language;
      },
    },
  },

  mounted() {},
  async created() {
    this.$i18n.locale = "it";
    this.setLanguage();

    // Clear any stale blocked state when landing on login without an active redirect.
    if (!this.$route.query?.blocked) {
      localStorage.removeItem("authMessage");
    }

    if (this.$route.query && this.$route.query.verification) {
      this.alert.value = true;
      this.alert.message = this.$route.query.verification;
      setTimeout(() => {
        this.alert.value = false;
        this.alert.message = "";
        //remove query from url
        this.$router.replace({
          path: this.$route.path,
          query: {},
        });
      }, 5000);
    }
  },
  methods: {
    checkAuthMessage() {
      const message = localStorage.getItem("authMessage");
      const fromBlockedRedirect =
        this.$route.query && String(this.$route.query.blocked) === "true";

      // Stale authMessage (e.g. tab closed before timeout) would flash on every
      // fresh visit — only show it when router sent us here with ?blocked=true.
      if (message && !fromBlockedRedirect) {
        localStorage.removeItem("authMessage");
        return;
      }

      if (!message && fromBlockedRedirect) {
        this.$router.replace({
          path: this.$route.path,
          query: {},
        });
        return;
      }

      if (message && fromBlockedRedirect) {
        this.alert.value = true;
        this.alert.message = message;

        setTimeout(() => {
          this.alert.value = false;
          this.alert.message = "";
          localStorage.removeItem("authMessage");
          this.$router.replace({
            path: this.$route.path,
            query: {},
          });
        }, 5000);
      }
    },
    async loginMethod() {
      if (!this.login.email || !this.login.password) return;
      this.loading = true;
      try {
        const res = await this.axios.post(
          `${this.$serverUri}/login`,
          this.login,
        );
        if (res.status === 200) {
          localStorage.setItem("exp", res.data.exp);
          localStorage.setItem("token", res.data.token);
          localStorage.setItem("language", this.language);
          this.$store.dispatch("languageAction", this.language);
          this.$router.push({ path: "/home" });
        }
      } catch (error) {
        this.alert.value = true;
        if (error && error.response && error.response.data) {
          this.alert.message = error.response.data;
        } else if (error && error.message) {
          this.alert.message = error.message;
        } else {
          this.alert.message = "Something went wrong";
        }
        setTimeout(() => {
          this.alert.value = false;
          this.alert.message = "";
        }, 3000);
      } finally {
        this.loading = false;
      }
    },

    setLanguage() {
      determineIpLocation()
        .then((response) => {
          const { country } = response;
          switch (country) {
            case "IT":
              this.language = "it";
              break;
            default:
              this.language = "it";
              break;
          }
        })
        .catch(() => {});
    },
    goToReset() {
      this.$router.push("/reset");
    },
  },
};
</script>

<style scoped>
html,
body,
#app {
  height: 100%;
  margin: 0;
}

.page {
  min-height: 100vh;
}

.bg-image {
  background-size: cover;
  background-position: center center;
  flex: 1;
}
</style>
